/**
 * Data layer for `24-7-tier-blind-support-with-published-sla`.
 *
 * Thin, typed wrappers over the REST surface exposed by the matching
 * backend route (`apps/api/src/routes/24-7-tier-blind-support-with-published-sla.ts`).
 *
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * Routes consumed:
 *   - GET  /api/v1/24-7-tier-blind-support-with-published-sla           → list
 *   - POST /api/v1/24-7-tier-blind-support-with-published-sla           → create
 *   - GET  /api/v1/24-7-tier-blind-support-with-published-sla/sla       → published SLA table
 *
 * No competitor names appear in any copy — "tier-blind" refers to the
 * commitment that every customer plan tier receives the same SLA.
 */

export type TicketPriority = "urgent" | "high" | "normal" | "low";

export type TicketStatus = "open" | "ack" | "resolved";

export interface TicketRow {
	id: string;
	tenant_id: string;
	client_ticket_id: string;
	subject: string;
	body: string;
	priority: TicketPriority;
	status: TicketStatus;
	sla_target_seconds: number;
	created_at: number;
	ack_at: number | null;
	resolved_at: number | null;
}

export interface ListTicketsResponse {
	items: TicketRow[];
}

export interface CreateTicketBody {
	client_ticket_id: string;
	subject: string;
	body: string;
	priority: TicketPriority;
}

export interface CreateTicketResponse {
	ok: boolean;
	client_ticket_id: string;
	priority: TicketPriority;
	sla_target_seconds: number;
	created_at: number;
}

export interface PublishedSlaResponse {
	tier_blind: boolean;
	response_seconds: Record<TicketPriority, number>;
	note: string;
}

const BASE = "/api/v1/24-7-tier-blind-support-with-published-sla";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listTickets(): Promise<ListTicketsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListTicketsResponse>(res);
}

export async function createTicket(
	body: CreateTicketBody,
): Promise<CreateTicketResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateTicketResponse>(res);
}

export async function fetchPublishedSla(): Promise<PublishedSlaResponse> {
	const res = await fetch(`${BASE}/sla`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<PublishedSlaResponse>(res);
}

// ---------------------------------------------------------------------------
// Derived helpers — pure, test-friendly.
// ---------------------------------------------------------------------------

export const PRIORITY_ORDER: TicketPriority[] = [
	"urgent",
	"high",
	"normal",
	"low",
];

export function priorityLabel(p: TicketPriority): string {
	switch (p) {
		case "urgent":
			return "Urgent";
		case "high":
			return "High";
		case "normal":
			return "Normal";
		case "low":
			return "Low";
	}
}

export function statusLabel(s: TicketStatus): string {
	switch (s) {
		case "open":
			return "Open";
		case "ack":
			return "Acknowledged";
		case "resolved":
			return "Resolved";
	}
}

/** Format response-target seconds as a compact human string. */
export function formatSlaWindow(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "—";
	if (seconds < 60) return `${Math.round(seconds)}s`;
	if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
	if (seconds < 86400) {
		const h = seconds / 3600;
		return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
	}
	const d = seconds / 86400;
	return Number.isInteger(d) ? `${d}d` : `${d.toFixed(1)}d`;
}

/** Format a unix timestamp (seconds) as a short relative-time label. */
export function formatCreatedAt(unixSec: number, nowSec?: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	const now = typeof nowSec === "number" ? nowSec : Math.floor(Date.now() / 1000);
	const delta = Math.max(0, now - unixSec);
	if (delta < 60) return "just now";
	if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
	if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
	return `${Math.floor(delta / 86400)}d ago`;
}

/** Derive the deadline (unix sec) from created_at + sla_target_seconds. */
export function slaDeadline(row: Pick<TicketRow, "created_at" | "sla_target_seconds">): number {
	return row.created_at + row.sla_target_seconds;
}

/** True if the SLA deadline has already passed relative to `now`. */
export function isSlaBreached(
	row: Pick<TicketRow, "created_at" | "sla_target_seconds" | "status">,
	nowSec?: number,
): boolean {
	if (row.status === "resolved") return false;
	const now = typeof nowSec === "number" ? nowSec : Math.floor(Date.now() / 1000);
	return now > slaDeadline(row);
}

/** Sort tickets: open breaches first, then open by priority, then resolved. */
export function sortTickets(rows: TicketRow[], nowSec?: number): TicketRow[] {
	const now = typeof nowSec === "number" ? nowSec : Math.floor(Date.now() / 1000);
	const prioRank: Record<TicketPriority, number> = {
		urgent: 0,
		high: 1,
		normal: 2,
		low: 3,
	};
	return [...rows].sort((a, b) => {
		const aOpen = a.status !== "resolved";
		const bOpen = b.status !== "resolved";
		if (aOpen !== bOpen) return aOpen ? -1 : 1;
		const aBr = aOpen && isSlaBreached(a, now);
		const bBr = bOpen && isSlaBreached(b, now);
		if (aBr !== bBr) return aBr ? -1 : 1;
		if (a.priority !== b.priority) return prioRank[a.priority] - prioRank[b.priority];
		return b.created_at - a.created_at;
	});
}

/** Count tickets matching a filter predicate — small helper for tiles. */
export function countBy<T>(rows: T[], pred: (r: T) => boolean): number {
	let n = 0;
	for (const r of rows) if (pred(r)) n++;
	return n;
}

export const supportTicketQueryKeys = {
	all: ["24-7-tier-blind-support-with-published-sla"] as const,
	list: () => ["24-7-tier-blind-support-with-published-sla", "list"] as const,
	sla: () => ["24-7-tier-blind-support-with-published-sla", "sla"] as const,
	detail: (id: string) =>
		["24-7-tier-blind-support-with-published-sla", "detail", id] as const,
};
